<template>
  <div class="scanner-root">
    <div class="scanner-history-wrapper">
      <span class="scanner-history-title">Check-in History</span>
      <div id="scanner-history-list" class="scanner-history-list">
        <div class="scanner-history-item" v-for="item in checkinHistory" :key="item.id">
          <img :src="item?.member?.file?.url || '/admin/media/users/blank.png'" alt="Profile Picture" />
          <div>
            <div class="name">{{ item.member_name }}</div>
            <div>{{ item.checkin_datetime | moment("DD MMM YYYY, HH:mm") }}</div>
            <div v-if="item.package_name">Paket: {{ item.package_name }}</div>
            <div v-if="item.membership_expired_date">
              Expired: {{ item.membership_expired_date | moment("DD/MM/YYYY") }}
            </div>
            <div v-if="item.fitness_consultant_name">FC: {{ item.fitness_consultant_name }}</div>
            <div v-if="item.personal_trainer_name">PT: {{ item.personal_trainer_name }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="scanner-wrapper">
      <video ref="video" autoplay muted playsinline></video>
  
      <div v-if="loading" class="overlay">Memvalidasi...</div>
      <div v-if="error" class="overlay error">{{ error }}</div>
      <div v-if="success" class="overlay success">
        ✅ Check-in Berhasil<br />
        <img
          v-if="scanResult.member_photo_url"
          :src="scanResult.member_photo_url"
          alt="Foto Member"
          class="scan-result-photo"
        />
        <div class="name">{{ scanResult.member_name }}</div>
        <div v-if="scanResult.package_name">Paket: {{ scanResult.package_name }}</div>
        <div v-if="scanResult.membership_expired_date">
          Expired: {{ scanResult.membership_expired_date | moment("DD/MM/YYYY") }}
        </div>
        <div v-if="scanResult.fitness_consultant_name">FC: {{ scanResult.fitness_consultant_name }}</div>
        <div v-if="scanResult.personal_trainer_name">PT: {{ scanResult.personal_trainer_name }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { BrowserMultiFormatReader } from '@zxing/library';
import ApiService from "@/core/services/api.service";

export default {
  data() {
    return {
      checkinHistory: [],
      memberId: '',
      signature: '',
      codeReader: null,
      loading: false,
      loadingData: false,
      error: '',
      success: false,
      scanResult: {},
    };
  },
  mounted() {
    this.codeReader = new BrowserMultiFormatReader();
    this.startScanner();
    this.getCheckinHistory();
  },
  beforeDestroy() {
    if (this.codeReader) {
      this.codeReader.reset();
    }
  },
  methods: {
    async startScanner() {
      try {
        const devices = await this.codeReader.listVideoInputDevices();
        const selectedDeviceId = devices[0]?.deviceId;

        this.codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          this.$refs.video,
          async (result) => {
            if (result) {
              const text = result.getText();
              try {
                await this.processQR(text);
              } catch (e) {
                this.error = 'QR tidak valid atau sudah kedaluwarsa.';
              }
              this.codeReader.reset(); // stop kamera
            }
          }
        );
      } catch (e) {
        this.error = 'Kamera tidak tersedia.';
      }
    },

    async processQR(qrText) {
      // Parsing format: ?memberId=xxx&signature=yyy
      const query = qrText.startsWith('?') ? qrText : '?' + qrText;
      const params = new URLSearchParams(query);
      const memberId = params.get('memberId');
      const signature = params.get('signature');

      if (!memberId || !signature) {
        throw new Error('Invalid QR data');
      }

      this.loading = true;
      this.error = '';
      this.success = false;

      try {
        const res = await fetch(`${process.env.VUE_APP_SERVER_URL}/validate-scanner`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            member_id: memberId,
            signature,
          }),
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Gagal validasi QR');
        }

        this.getCheckinHistory();

        this.success = true;
        this.memberId = memberId;
        this.scanResult = data;
      } catch (e) {
        this.error = e.message || 'Terjadi kesalahan';
      } finally {
        this.loading = false;
        // Restart scanner setelah delay 2 detik
        setTimeout(() => {
          this.success = false;
          this.error = '';
          this.scanResult = {};
          this.startScanner();
        }, 2000);
      }
    },

    async getCheckinHistory() {
      this.loadingData = true;
      await ApiService.post("checkin-member/data", {
        "columns": [
            {
                "data": "id",
                "sortable": false,
                "searchable": false
            },
            {
                "data": "checkin_date",
                "sortable": true,
                "searchable": false
            },
            {
                "data": "start_time",
                "sortable": true,
                "searchable": true
            },
            {
                "data": "member_name",
                "sortable": true,
                "searchable": true
            },
            {
                "data": "personal_trainer_name",
                "sortable": true,
                "searchable": true
            },
            {
                "data": "profile_picture",
                "sortable": false,
                "searchable": false
            },
            {
                "data": "package_name",
                "sortable": false,
                "searchable": false
            },
            {
                "data": "membership_expired_date",
                "sortable": false,
                "searchable": false
            },
            {
                "data": "fitness_consultant_name",
                "sortable": false,
                "searchable": false
            }
        ],
        "page": 1,
        "perPage": 50
      })
        .then(({ data }) => {
          this.checkinHistory = data.data;
          this.loadingData = false;
          const scannerHistoryList = document.getElementById('scanner-history-list');
          scannerHistoryList.scrollTop = 0;
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
  },
};
</script>

<style scoped>
.scanner-root {
  display: flex;
  gap: 16px;
}

.scanner-history-wrapper {
  padding-top: 16px;
  width: 40%;
  min-width: 300px;
  height: calc(100vh - 150px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.scanner-history-title {
  margin: 16px 16px 0 16px;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.scanner-history-list {
  padding: 16px;
  padding-bottom: 32px;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 16px;
}

.scanner-history-item {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
}

.scanner-history-item img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 12px 0 0 12px;
}

.scanner-history-item .name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.scanner-history-item > div > div:not(.name) {
  font-size: 0.85rem;
  color: #666;
}

.scanner-wrapper {
  position: relative;
  width: 100%;
  height: calc(100vh - 150px);
  background: black;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.overlay {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.2rem;
  text-align: center;
  z-index: 10;
}

.overlay.error {
  background: rgba(255, 0, 0, 0.85);
  color: white;
}

.overlay.success {
  background: rgba(0, 128, 0, 0.85);
  color: white;
}

.overlay.success .scan-result-photo {
  width: 90px;
  height: 90px;
  object-fit: cover;
  border-radius: 50%;
  margin: 8px 0;
}

.overlay.success .name {
  font-size: 1.2rem;
  font-weight: 700;
}

.overlay.success div {
  font-size: 0.95rem;
  font-weight: normal;
}
</style>
